import { Button } from "@gigpro/ui/Button";
import { Chip } from "@gigpro/ui/Chip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@gigpro/ui/Collapsible";
import { Form } from "@gigpro/ui/Form";
import { Textarea } from "@gigpro/ui/Form/Textarea";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { ChevronDown } from "@gigpro/ui/Icons";
import { Skeleton } from "@gigpro/ui/Skeleton";
import { toast } from "@gigpro/ui/Toast";
import { useNow } from "@gigpro/ui/utils/useNow";
import { formatDate } from "@gigpro/utils/datetime";
import { ApiError } from "@sidegigllc/api";
import { useUsersNotesCreate } from "@sidegigllc/api/client";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { differenceInDays, formatDistanceToNow } from "date-fns";
import { ProNotesTable } from "domains/admin/containers/ProNotesTable";
import { notesQuery } from "domains/admin/containers/ProNotesTable/queries";
import React from "react";
import * as z from "zod/mini";

type ProNotesProps = { proId: number };

export function ProNotes({ proId }: Readonly<ProNotesProps>) {
  return (
    <div className="flex flex-col gap-4 pt-4">
      <AddNoteForm proId={proId} />
      <ProNotesTable proId={proId} />
    </div>
  );
}

const notesShown = 5;
export function ProNotesView({ proId }: Readonly<ProNotesProps>) {
  const { data: notes, isLoading } = useQuery(notesQuery({ proId }));
  const now = useNow();
  const hasNotes = notes?.length != null && notes.length > 0;
  const visibleNotes = hasNotes ? notes.slice(0, notesShown) : [];

  return (
    <Collapsible className="flex flex-col gap-2">
      <CollapsibleTrigger className="group flex items-center gap-1">
        <h2 className="text-large font-semibold">Comments</h2>
        {notes == null ? (
          <Skeleton variant="circle" className="size-3.5" />
        ) : (
          <Chip className="bg-brand px-1 py-0 text-white">{notes.length}</Chip>
        )}
        <ChevronDown className="ml-auto size-4 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-2">
        {hasNotes ? (
          <>
            {visibleNotes.map((note) => {
              const distanceSinceCreation = differenceInDays(now, note.created_ts);
              const isNew = distanceSinceCreation < 7;
              return (
                <div key={note.id}>
                  <p className="flex items-center gap-1.5 text-small text-gray">
                    <strong>
                      {note.created_by_user.first_name} {note.created_by_user.last_name}
                    </strong>
                    {isNew
                      ? formatDistanceToNow(note.created_ts, { addSuffix: true })
                      : formatDate(note.created_ts, "EEE, MMM d")}
                  </p>
                  <p>
                    {/* render notes with line breaks */}
                    {note.note.split("\n").map((line, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <React.Fragment key={index}>
                        {line}
                        {index < note.note.split("\n").length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              );
            })}
            {notes.length > notesShown && (
              <Button asChild size="small">
                <Link to="/admin/pros/$id/notes" params={{ id: String(proId) }}>
                  View {notes.length - notesShown} more
                </Link>
              </Button>
            )}
          </>
        ) : (
          <p className="text-gray">{isLoading ? "Loading..." : "Be the first to leave a comment!"}</p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

const maxNoteLength = 1500;
const formSchema = z.object({ note: z.string().check(z.maxLength(maxNoteLength)) });
type FormValues = z.infer<typeof formSchema>;

export function AddNoteForm({ proId }: Readonly<ProNotesProps>) {
  const userNotes = useQuery(notesQuery({ proId }));
  const createNote = useUsersNotesCreate();

  const form = useZodForm({ schema: formSchema });

  const onSubmit = async (payload: FormValues) => {
    try {
      await createNote.mutateAsync({ id: proId, data: payload });
      toast.success("Note added");
      form.reset();
      await userNotes.refetch();
    } catch (error) {
      ApiError.fromThrown(error).setFormErrors(form.setError);
    }
  };
  return (
    <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-1">
      <Textarea
        control={form.control}
        name="note"
        label="Leave a comment"
        placeholder="Start typing.."
        maxLength={maxNoteLength}
      />
      <Button type="submit" variant="contained" size="small" loading={form.formState.isSubmitting} className="self-end">
        Submit
      </Button>
    </Form>
  );
}
